import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const LIST_PAGE_SIZE = 10;

interface ShowMoreFooterProps {
  /** Rows not yet visible. */
  hiddenCount: number;
  /** Rows currently visible. */
  visibleCount: number;
  onShowMore: () => void;
  onShowLess: () => void;
  pageSize?: number;
}

/**
 * Shared table footer for incrementally paged dashboard lists:
 * "Show N more" (+ total remaining) beside "Show less" once expanded.
 * Renders nothing when the list fits in one page.
 */
export function ShowMoreFooter({
  hiddenCount,
  visibleCount,
  onShowMore,
  onShowLess,
  pageSize = LIST_PAGE_SIZE,
}: ShowMoreFooterProps) {
  const { t } = useTranslation('common');
  if (hiddenCount <= 0 && visibleCount <= pageSize) return null;
  return (
    <div className="flex border-t border-slate-200 bg-slate-50/60">
      {hiddenCount > 0 && (
        <button
          onClick={onShowMore}
          className="flex flex-1 flex-col items-center gap-0.5 px-3 py-2 transition-colors hover:bg-slate-100 cursor-pointer"
        >
          <span className="flex items-center gap-1.5 text-[0.8125rem] font-medium text-[#0069D1]">
            <ChevronDown size={14} />
            {t('dashboard.showMore', { count: Math.min(pageSize, hiddenCount) })}
          </span>
          {hiddenCount > pageSize && (
            <span className="text-xs text-slate-400">
              {t('dashboard.showAllCount', { count: hiddenCount })}
            </span>
          )}
        </button>
      )}
      {visibleCount > pageSize && (
        <button
          onClick={onShowLess}
          className="flex flex-1 items-center justify-center gap-1.5 border-l border-slate-200 px-3 py-2 text-[0.8125rem] font-medium text-[#0069D1] transition-colors hover:bg-slate-100 cursor-pointer first:border-l-0"
        >
          <ChevronUp size={14} />
          {t('dashboard.showLess')}
        </button>
      )}
    </div>
  );
}
